import { useState } from 'react';
import { motion } from 'framer-motion';
import { DollarSign, ShieldCheck, Repeat2 } from 'lucide-react';
import confetti from 'canvas-confetti';

const features = [
  {
    icon: DollarSign,
    title: 'You Set the Budget',
    description:
      'Tell us your project scope and budget. We match you with the right talent and deliver within your constraints.',
  },
  {
    icon: ShieldCheck,
    title: 'No Hidden Fees',
    description:
      'What you see is what you pay. No surprise charges, no platform fees, no markup games. Ever.',
  },
  {
    icon: Repeat2,
    title: 'Pay As You Scale',
    description:
      'Start small, scale fast. No long-term contracts, no minimum commitments. Adjust capacity as needs change.',
  },
];

export const PricingSection = () => {
  const [budget, setBudget] = useState('');
  const [result, setResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  const calculate = () => {
    const n = parseFloat(budget);
    if (!n || n <= 0) return;
    setResult(null);
    setCalculating(true);
    setTimeout(() => {
      setCalculating(false);
      setResult({ budget: n });
      confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.6 },
        colors: ['#0071E3', '#34C759', '#FF9F0A', '#FF375F', '#BF5AF2'],
      });
    }, 3000);
  };

  return (
    <section id="pricing" className="py-24 md:py-32 bg-apple-surface border-t border-border">
      <div className="section-container">
        {/* Header */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="section-label mb-5">Transparent Costs</p>
          <h2 className="text-[42px] sm:text-[52px] font-extrabold tracking-tighter text-foreground leading-none">
            Simple, transparent,<br />
            <span className="text-apple-grey font-semibold">scalable pricing.</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-[1fr_400px] gap-12 items-start">
          {/* Feature columns */}
          <div className="grid sm:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                className="bg-background rounded-2xl p-6 border border-border flex flex-col"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
              >
                <div className="w-9 h-9 rounded-xl bg-apple-blue-light flex items-center justify-center mb-4">
                  <f.icon size={16} className="text-apple-blue" />
                </div>
                <h3 className="text-[16px] font-bold text-foreground mb-2 leading-snug">{f.title}</h3>
                <p className="text-[14px] text-muted-foreground leading-[1.6] mt-auto">{f.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Calculator */}
          <motion.div
            className="bg-background rounded-3xl p-8 border border-border"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <h3 className="text-[20px] font-bold text-foreground mb-2 tracking-tight">Calculate Your Cost</h3>
            <p className="text-[14px] text-muted-foreground mb-6 leading-relaxed">
              Enter your project budget to see what you can deliver with HireX8.
            </p>

            <div className="mb-5">
              <label className="section-label mb-2 block">Your Project Budget (USD)</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground text-[16px] font-medium">$</span>
                <input
                  type="number"
                  value={budget}
                  onChange={e => setResult(null) || setBudget(e.target.value)}
                  placeholder="5,000"
                  className="w-full pl-9 pr-4 py-3.5 rounded-xl border border-border bg-background text-foreground text-[16px] font-medium focus:outline-none focus:ring-2 focus:ring-apple-blue/30 focus:border-apple-blue transition-all"
                />
              </div>
            </div>

            <button
              onClick={calculate}
              className="btn-primary w-full justify-center"
            >
              Calculate
            </button>

            {calculating && (
              <motion.div
                className="mt-6 flex flex-col items-center gap-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className="w-8 h-8 rounded-full border-2 border-apple-blue border-t-transparent animate-spin" />
                <p className="text-[13px] text-muted-foreground">Calculating your cost…</p>
              </motion.div>
            )}

            {result && !calculating && (
              <motion.div
                className="mt-6"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="swiss-divider mb-5" />
                <div className="mb-4 flex justify-between items-center">
                  <span className="text-[14px] text-muted-foreground">Your budget</span>
                  <span className="text-[20px] font-extrabold text-apple-blue">${result.budget.toLocaleString()}</span>
                </div>
                <div className="p-5 rounded-2xl bg-apple-blue-light border border-apple-blue/20 text-center">
                  <p className="text-[17px] font-extrabold text-foreground leading-snug mb-1">
                    Your budget is your cost.
                  </p>
                  <p className="text-[14px] font-semibold text-apple-blue leading-snug">
                    HireX8 is zero platform fees<sup>*</sup> for agencies
                  </p>
                </div>
                <p className="text-[11px] text-muted-foreground mt-3 text-center leading-relaxed">
                  * 3.5% fee on credit card processing or a flat $5 via ACH Direct
                </p>
              </motion.div>
            )}
          </motion.div>
        </div>

        <motion.div
          className="mt-12 text-center"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <a href="https://vibe.hirex8.com/register?type=client" className="btn-primary inline-flex">
            Build Your Team Now
          </a>
          <p className="text-[13px] text-muted-foreground mt-3">No contracts. No commitments. Cancel anytime.</p>
        </motion.div>
      </div>
    </section>
  );
};
